import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    TextField,
    IconButton,
    CircularProgress,
    Alert,
    FormControlLabel,
    Switch,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Divider,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import {
    Save as SaveIcon,
    ArrowBack as BackIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    ExpandMore as ExpandMoreIcon,
    DragIndicator as DragIcon,
    AutoAwesome as AiIcon,
    Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { questionnaireService, IQuestionnaire, IQuestionnaireStep, IQuestionnaireField, IGeneratedQuestionnaireDraft } from '../services/questionnaireService';
import GenerateQuestionnaireModal from '../components/GenerateQuestionnaireModal';
import api from '../services/api';

const FIELD_TYPES = [
    { value: 'text', label: 'Text' },
    { value: 'textarea', label: 'Textarea' },
    { value: 'number', label: 'Number' },
    { value: 'email', label: 'Email' },
    { value: 'date', label: 'Date' },
    { value: 'dropdown', label: 'Dropdown' },
    { value: 'select', label: 'Select' },
    { value: 'checkbox', label: 'Checkbox' },
    { value: 'display', label: 'Display Only' },
    { value: 'file', label: 'File Upload' },
];

// Helper to generate semantic ID from step title + label (matching AdminWelcomeQuestionnaire format)
const generateSemanticId = (stepTitle: string, label: string): string => {
    const sectionPart = stepTitle.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
    const labelPart = label.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
    return `${sectionPart}_${labelPart}`.replace(/_+/g, '_').replace(/^_+|_+$/g, '');
};

// Legacy ID generator for step IDs (still needed for steps)
const generateStepId = () => `step_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Stable internal key generator for React component keys (won't change during editing)
const generateInternalKey = () => `_key_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const QuestionnaireEditor: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();
    const queryClient = useQueryClient();
    const isEditing = Boolean(id);
    const copyFromWelcome = searchParams.get('copyFromWelcome') === 'true';
    const initialDraft = (location.state as any)?.initialDraft as IGeneratedQuestionnaireDraft | undefined;

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        isDefault: false,
        isActive: false,
        steps: [] as IQuestionnaireStep[],
    });
    const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
    const [expandedStep, setExpandedStep] = useState<string | false>(false);
    const [generateModalOpen, setGenerateModalOpen] = useState(false);
    const [confirmOverwriteOpen, setConfirmOverwriteOpen] = useState(false);
    const [pendingDraft, setPendingDraft] = useState<IGeneratedQuestionnaireDraft | null>(null);

    const applyDraft = (draft: IGeneratedQuestionnaireDraft) => {
        const stepsWithInternalKeys = draft.steps.map(step => ({
            ...step,
            id: step.id || generateStepId(),
            fields: step.fields.map(field => ({
                ...field,
                _internalKey: (field as any)._internalKey || generateInternalKey(),
            })),
        }));

        setFormData(prev => ({
            ...prev,
            title: draft.title || prev.title,
            description: draft.description !== undefined ? draft.description : prev.description,
            steps: stepsWithInternalKeys,
        }));
        setNotification({
            type: 'success',
            message: `Successfully generated ${draft.steps.length} step(s) with AI! Review and adjust before saving.`
        });
        setPendingDraft(null);
        setConfirmOverwriteOpen(false);
    };

    const handleGeneratedDraft = (draft: IGeneratedQuestionnaireDraft) => {
        if (formData.steps.length > 0) {
            setPendingDraft(draft);
            setConfirmOverwriteOpen(true);
        } else {
            applyDraft(draft);
        }
    };

    // Fetch existing questionnaire if editing
    const { data: existingQuestionnaire, isLoading: loadingExisting } = useQuery<IQuestionnaire>({
        queryKey: ['questionnaire', id],
        queryFn: () => questionnaireService.getQuestionnaireById(id!),
        enabled: isEditing
    });

    // Fetch welcome questionnaire if copying
    const { data: welcomeQuestionnaire, isLoading: loadingWelcome } = useQuery({
        queryKey: ['welcomeQuestionnaire'],
        queryFn: async () => {
            const response = await api.get('/welcome-questionnaire/definition');
            return response.data;
        },
        enabled: copyFromWelcome && !isEditing
    });

    // Initialize form data
    useEffect(() => {
        // Helper to add stable internal keys to fields
        const addInternalKeysToSteps = (steps: IQuestionnaireStep[]) => {
            return steps.map(step => ({
                ...step,
                fields: step.fields.map(field => ({
                    ...field,
                    _internalKey: (field as any)._internalKey || generateInternalKey(),
                })),
            }));
        };

        if (existingQuestionnaire) {
            setFormData({
                title: existingQuestionnaire.title,
                description: existingQuestionnaire.description || '',
                isDefault: existingQuestionnaire.isDefault,
                isActive: existingQuestionnaire.isActive,
                steps: addInternalKeysToSteps(existingQuestionnaire.steps),
            });
        } else if (initialDraft && !isEditing) {
            setFormData({
                title: initialDraft.title || 'Welcome Questionnaire Draft',
                description: initialDraft.description || '',
                isDefault: false,
                isActive: false,
                steps: addInternalKeysToSteps(initialDraft.steps || []),
            });
            setNotification({
                type: 'success',
                message: `Loaded AI-generated questionnaire draft with ${initialDraft.steps.length} step(s)! Review and adjust before saving.`
            });
        } else if (welcomeQuestionnaire && copyFromWelcome) {
            setFormData({
                title: 'Copy of Welcome Questionnaire',
                description: 'Copied from the default welcome questionnaire',
                isDefault: false,
                isActive: false,
                steps: addInternalKeysToSteps(welcomeQuestionnaire.steps || []),
            });
        }
    }, [existingQuestionnaire, welcomeQuestionnaire, copyFromWelcome, initialDraft, isEditing]);

    // Save mutation
    const saveMutation = useMutation({
        mutationFn: async (data: typeof formData) => {
            if (isEditing) {
                return questionnaireService.updateQuestionnaire(id!, data);
            } else {
                return questionnaireService.createQuestionnaire(data);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['questionnaires'] });
            setNotification({ type: 'success', message: isEditing ? 'Questionnaire updated' : 'Questionnaire created' });
            setTimeout(() => navigate('/questionnaire-library'), 1000);
        },
        onError: () => {
            setNotification({ type: 'error', message: 'Failed to save questionnaire' });
        }
    });

    const handleSave = () => {
        if (!formData.title.trim()) {
            setNotification({ type: 'error', message: 'Title is required' });
            return;
        }
        if (formData.steps.length === 0) {
            setNotification({ type: 'error', message: 'At least one step is required' });
            return;
        }
        saveMutation.mutate(formData);
    };

    // Step management
    const addStep = () => {
        const newStep: IQuestionnaireStep = {
            id: generateStepId(),
            section: `Step ${formData.steps.length + 1}`,
            title: 'New Step',
            description: '',
            fields: [],
        };
        setFormData(prev => ({
            ...prev,
            steps: [...prev.steps, newStep]
        }));
        setExpandedStep(newStep.id);
    };

    const updateStep = (stepId: string, updates: Partial<IQuestionnaireStep>) => {
        setFormData(prev => ({
            ...prev,
            steps: prev.steps.map(step =>
                step.id === stepId ? { ...step, ...updates } : step
            )
        }));
    };

    const deleteStep = (stepId: string) => {
        setFormData(prev => ({
            ...prev,
            steps: prev.steps.filter(step => step.id !== stepId)
        }));
    };

    // Field management
    const addField = (stepId: string) => {
        const step = formData.steps.find(s => s.id === stepId);
        const stepTitle = step?.title || 'general';
        const defaultLabel = 'New Field';
        const semanticId = generateSemanticId(stepTitle, defaultLabel);
        const newField: IQuestionnaireField & { _internalKey?: string } = {
            id: semanticId,
            label: defaultLabel,
            type: 'text',
            required: false,
            _internalKey: generateInternalKey(), // Stable key for React rendering
        };
        updateStep(stepId, {
            fields: [...(step?.fields || []), newField]
        });
    };

    const updateField = (stepId: string, fieldId: string, updates: Partial<IQuestionnaireField>) => {
        const step = formData.steps.find(s => s.id === stepId);
        if (!step) return;

        const currentField = step.fields.find(f => f.id === fieldId);
        if (!currentField) return;

        const stepTitle = step.title || 'general';

        // If label is being updated, auto-update ID if it was auto-generated
        if (updates.label !== undefined) {
            const currentAutoId = generateSemanticId(stepTitle, currentField.label);
            const newAutoId = generateSemanticId(stepTitle, updates.label);
            // Only update ID if it currently matches the auto-generated pattern
            if (currentField.id === currentAutoId || currentField.id.startsWith('field_')) {
                updates.id = newAutoId;
            }
        }

        updateStep(stepId, {
            fields: step.fields.map(field =>
                field.id === fieldId ? { ...field, ...updates } : field
            )
        });
    };

    const deleteField = (stepId: string, fieldId: string) => {
        const step = formData.steps.find(s => s.id === stepId);
        if (!step) return;
        updateStep(stepId, {
            fields: step.fields.filter(field => field.id !== fieldId)
        });
    };

    if (loadingExisting || loadingWelcome) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <IconButton onClick={() => navigate('/questionnaire-library')}>
                    <BackIcon />
                </IconButton>
                <Typography variant="h4" sx={{ fontWeight: 600, flex: 1 }}>
                    {isEditing ? 'Edit Questionnaire' : 'Create Questionnaire'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Button
                        variant="outlined"
                        startIcon={<AiIcon />}
                        onClick={() => setGenerateModalOpen(true)}
                    >
                        Generate from File (AI)
                    </Button>
                    {isEditing && id && (
                        <Button
                            variant="outlined"
                            startIcon={<VisibilityIcon />}
                            onClick={() => navigate(`/questionnaire-library/preview/${id}`)}
                            disabled={formData.steps.length === 0}
                        >
                            View Questionnaire
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        disabled={saveMutation.isPending}
                        sx={{ bgcolor: '#667eea' }}
                    >
                        {saveMutation.isPending ? 'Saving...' : 'Save'}
                    </Button>
                </Box>
            </Box>

            {notification && (
                <Alert severity={notification.type} sx={{ mb: 3 }} onClose={() => setNotification(null)}>
                    {notification.message}
                </Alert>
            )}

            {/* Basic Info */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Basic Information</Typography>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Title"
                            value={formData.title}
                            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                            required
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.isActive}
                                        onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                                        color="success"
                                    />
                                }
                                label="Mark as Active Welcome Questionnaire"
                            />
                            <Typography variant="caption" color="text.secondary">
                                When active, all clients under your account must complete this questionnaire before accessing the platform.
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Description"
                            value={formData.description}
                            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                            multiline
                            rows={2}
                        />
                    </Grid>
                </Grid>
            </Paper>

            {/* Steps */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                    Steps ({formData.steps.length})
                </Typography>
                <Button startIcon={<AddIcon />} onClick={addStep} variant="outlined">
                    Add Step
                </Button>
            </Box>

            {formData.steps.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary" sx={{ mb: 2 }}>
                        No steps yet. Add your first step to get started.
                    </Typography>
                    <Button startIcon={<AddIcon />} onClick={addStep} variant="contained">
                        Add First Step
                    </Button>
                </Paper>
            ) : (
                formData.steps.map((step, stepIndex) => (
                    <Accordion
                        key={step.id}
                        expanded={expandedStep === step.id}
                        onChange={(_, isExpanded) => setExpandedStep(isExpanded ? step.id : false)}
                        sx={{ mb: 2 }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                                <DragIcon sx={{ color: 'text.secondary' }} />
                                <Chip label={`Step ${stepIndex + 1}`} size="small" />
                                <Typography sx={{ fontWeight: 500 }}>{step.title}</Typography>
                                <Chip label={`${step.fields.length} fields`} size="small" variant="outlined" />
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="Section Name"
                                        value={step.section}
                                        onChange={(e) => updateStep(step.id, { section: e.target.value })}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="Title"
                                        value={step.title}
                                        onChange={(e) => updateStep(step.id, { title: e.target.value })}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="Description"
                                        value={step.description || ''}
                                        onChange={(e) => updateStep(step.id, { description: e.target.value })}
                                        size="small"
                                    />
                                </Grid>
                            </Grid>

                            <Divider sx={{ my: 2 }} />

                            {step.title.toLowerCase().includes('review') && step.fields.length === 0 && (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    <strong>Section Review Step:</strong> This step automatically displays an interactive summary card of all questions answered in this section with <em>"Edit"</em> shortcuts so clients can review and correct answers before proceeding.
                                </Alert>
                            )}

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="subtitle2">Fields ({step.fields.length})</Typography>
                                <Button size="small" startIcon={<AddIcon />} onClick={() => addField(step.id)}>
                                    Add Field
                                </Button>
                            </Box>

                            {step.fields.map((field) => (
                                <Paper key={(field as any)._internalKey || field.id} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} md={3}>
                                            <TextField
                                                fullWidth
                                                label="Label"
                                                value={field.label}
                                                onChange={(e) => updateField(step.id, field.id, { label: e.target.value })}
                                                size="small"
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={2}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Type</InputLabel>
                                                <Select
                                                    value={field.type}
                                                    label="Type"
                                                    onChange={(e) => updateField(step.id, field.id, { type: e.target.value as IQuestionnaireField['type'] })}
                                                >
                                                    {FIELD_TYPES.map(type => (
                                                        <MenuItem key={type.value} value={type.value}>
                                                            {type.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <TextField
                                                fullWidth
                                                label="Placeholder"
                                                value={field.placeholder || ''}
                                                onChange={(e) => updateField(step.id, field.id, { placeholder: e.target.value })}
                                                size="small"
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={2}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={field.required}
                                                        onChange={(e) => updateField(step.id, field.id, { required: e.target.checked })}
                                                        size="small"
                                                    />
                                                }
                                                label="Required"
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={2} sx={{ textAlign: 'right' }}>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => deleteField(step.id, field.id)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Grid>
                                        {(field.type === 'dropdown' || field.type === 'select') && (
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="Options (comma-separated)"
                                                    value={(field.options || []).join(', ')}
                                                    onChange={(e) => updateField(step.id, field.id, {
                                                        options: e.target.value.split(',').map(o => o.trim()).filter(Boolean)
                                                    })}
                                                    size="small"
                                                    helperText="Enter options separated by commas"
                                                />
                                            </Grid>
                                        )}
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Question Appearance (optional)"
                                                value={field.questionAppearance || ''}
                                                onChange={(e) => updateField(step.id, field.id, { questionAppearance: e.target.value })}
                                                size="small"
                                                placeholder="Display name in Form Builder..."
                                                helperText="If set, this will be shown in Form Builder instead of the label"
                                            />
                                        </Grid>
                                    </Grid>
                                </Paper>
                            ))}

                            {step.fields.length === 0 && (
                                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                                    No fields in this step yet.
                                </Typography>
                            )}

                            <Divider sx={{ my: 2 }} />

                            <Box sx={{ textAlign: 'right' }}>
                                <Button
                                    color="error"
                                    startIcon={<DeleteIcon />}
                                    onClick={() => deleteStep(step.id)}
                                >
                                    Delete Step
                                </Button>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                ))
            )}

            <GenerateQuestionnaireModal
                open={generateModalOpen}
                onClose={() => setGenerateModalOpen(false)}
                onGenerated={handleGeneratedDraft}
            />

            {/* Overwrite Confirmation Dialog */}
            <Dialog open={confirmOverwriteOpen} onClose={() => setConfirmOverwriteOpen(false)}>
                <DialogTitle>Replace Current Questionnaire?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Generating a questionnaire from a file will replace your currently loaded {formData.steps.length} step(s).
                        Are you sure you want to proceed?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setConfirmOverwriteOpen(false); setPendingDraft(null); }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        sx={{ bgcolor: '#667eea' }}
                        onClick={() => { if (pendingDraft) applyDraft(pendingDraft); }}
                    >
                        Replace & Load Draft
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default QuestionnaireEditor;
